import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { replace } from "redux-first-history";
import ym from "react-yandex-metrika";

export enum SidebarOption {
  OrderAdvertisement = "orderAdvertisement",
  Calendar = "calendar",
  AdvertisementPrices = "advertisementPrices",
  Profile = "profile",
  Notifications = "notifications",
  Tutorial = "tutorial",
  FAQ = "faq",
  Support = "support",
  TermsAndConditions = "termsAndConditions",
  Council = "council",
  Logout = "logout",
}

export enum AppPage {
  OrdersList = "OrdersList",
  Calendar = "Calendar",
  AdvertisementPrices = "AdvertisementPrices",
  SubmitAd = "SubmitAd",
  OrderDetail = "OrderDetail",
  AdTimeSelect = "AdTimeSelect",
  Invoicing = "Invoicing",
  AuthRequestDetail = "AuthRequestDetail",
  Tutorial = "Tutorial",
  TutorialArticles = "TutorialArticles",
  TutorialVideos = "TutorialVideos",
  StopCampaign = "StopCampaign",
}

export enum OrganizationAuthSteps {
  BasicInformation = 1,
  RegistrationInformation,
  Documents,
}

export interface NavigationStore {
  sidebar: SidebarOption;
  appPage?: AppPage;
  organizationAuthStep: OrganizationAuthSteps;
  tabs: { [key: string]: string };
  navigationCallBack?: () => void;
}

export const InitialNavigationState: NavigationStore = {
  sidebar: SidebarOption.OrderAdvertisement,
  appPage: undefined,
  organizationAuthStep: OrganizationAuthSteps.BasicInformation,
  tabs: {},
};

const NavigationSlice = createSlice({
  name: "NavigationSlice",
  initialState: InitialNavigationState,
  reducers: {
    setNavigationCallback(state, action: PayloadAction<() => void>) {
      state.navigationCallBack = action.payload;
    },
    setSidebarOption(state, action: PayloadAction<SidebarOption>) {
      state.sidebar = action.payload;
    },
    setTabState(state, action: PayloadAction<{ key: string; value: string }>) {
      state.tabs[action.payload.key] = action.payload.value;
    },
    setAppPageWithoutRedirect(state, action: PayloadAction<AppPage>) {
      state.appPage = action.payload;
    },
    setOrganizationAuthStepsWithoutRedirect(
      state,
      action: PayloadAction<OrganizationAuthSteps>
    ) {
      state.organizationAuthStep = action.payload;
    },
    resetState() {
      return InitialNavigationState;
    },
  },
});

export const AppPageUrls = {
  [AppPage.OrdersList]: "/home",
  [AppPage.Calendar]: "/calendar",
  [AppPage.AdvertisementPrices]: "/pricing",
  [AppPage.SubmitAd]: "/order",
  [AppPage.OrderDetail]: "/order_detail",
  [AppPage.AdTimeSelect]: "/time_select",
  [AppPage.Invoicing]: "/invoice",
  [AppPage.AuthRequestDetail]: "/invoice_profile",
  [AppPage.Tutorial]: "/tutorial",
  [AppPage.TutorialArticles]: "/tutorialArticles",
  [AppPage.TutorialVideos]: "/tutorialVideos",
};

const setAppPage = (page: AppPage) => {
  return (dispatch: any) => {
    const newUrl = AppPageUrls[page as keyof typeof AppPageUrls];
    ym("hit", newUrl);
    dispatch(NavigationActions.setAppPageWithoutRedirect(page));
    dispatch(replace(newUrl));

    switch (page) {
      case AppPage.Tutorial:
      case AppPage.TutorialArticles:
      case AppPage.TutorialVideos:
        dispatch(NavigationActions.setSidebarOption(SidebarOption.Tutorial));
        break;

      case AppPage.AdvertisementPrices:
        dispatch(
          NavigationActions.setSidebarOption(SidebarOption.AdvertisementPrices)
        );
        break;

      default:
        dispatch(
          NavigationActions.setSidebarOption(SidebarOption.OrderAdvertisement)
        );
    }
  };
};

export const OrganizationAuthStepsUrls = {
  [OrganizationAuthSteps.BasicInformation]: "/info",
  [OrganizationAuthSteps.RegistrationInformation]: "/company_info",
  [OrganizationAuthSteps.Documents]: "/documents",
};

const setOrganizationAuthSteps = (step: OrganizationAuthSteps) => {
  return (dispatch: any) => {
    const newUrl = OrganizationAuthStepsUrls[step];
    ym("hit", newUrl);
    dispatch(NavigationActions.setOrganizationAuthStepsWithoutRedirect(step));
    dispatch(replace(newUrl));
  };
};

export const NavigationActions = {
  ...NavigationSlice.actions,
  setAppPage,
  setOrganizationAuthSteps,
};
export const NavigationReducer = NavigationSlice.reducer;
